import type { ProfileImportPlan } from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { anaestheticRepository } from '../../db/repositories/anaesthetic.ts';
import { casesRepository } from '../../db/repositories/cases.ts';
import { edtRepository } from '../../db/repositories/edt.ts';
import { proceduresRepository } from '../../db/repositories/procedures.ts';

/**
 * Writing a confirmed import plan.
 *
 * One `importBatchId` across every kind of record the plan produced, so undoing
 * an import is one action rather than four. A trainee who imports 1,296
 * anaesthetic cases and then thinks better of it must be able to take it back
 * without picking through an entry list.
 *
 * Each repository writes its own kind in its own transaction. Not one
 * transaction across all four: Dexie's limit is five tables and this would need
 * six with the outbox, and — more to the point — a partial import that wrote
 * the cases but not the EDT entries is recoverable by re-importing, while a
 * transaction that failed at 1,296 records and rolled everything back would
 * leave the trainee with nothing and no clue why.
 */

export type ImportOutcome = {
  batchId: string;
  cases: number;
  procedures: number;
  edtEntries: number;
  anaestheticCases: number;
  total: number;
};

export async function applyProfileImport(
  userId: string,
  plan: ProfileImportPlan,
  sharedBatchId?: string,
): Promise<ImportOutcome> {
  const batchId = sharedBatchId ?? uuidv7();

  // Cases first, so their nested procedures land with them.
  const fromCases = await casesRepository.importBatch(userId, plan.cases, batchId);
  const standalone = await proceduresRepository.importBatch(userId, plan.procedures, batchId);
  const edtEntries = await edtRepository.importBatch(userId, plan.edtEntries, batchId);
  const anaestheticCases = await anaestheticRepository.importBatch(
    userId,
    plan.anaestheticCases,
    batchId,
  );

  const procedures = fromCases.procedures + standalone;

  return {
    batchId,
    cases: fromCases.cases,
    procedures,
    edtEntries,
    anaestheticCases,
    total: fromCases.cases + procedures + edtEntries + anaestheticCases,
  };
}

/**
 * Several sheets of one workbook, imported together under a single batch id.
 *
 * One id, because to the trainee this was one action: they dropped a workbook
 * in and said yes. Undoing it must take back all of it, not the last sheet.
 */
export async function applyProfileImports(
  userId: string,
  plans: readonly ProfileImportPlan[],
): Promise<ImportOutcome> {
  const batchId = uuidv7();

  const totals: ImportOutcome = {
    batchId,
    cases: 0,
    procedures: 0,
    edtEntries: 0,
    anaestheticCases: 0,
    total: 0,
  };

  for (const plan of plans) {
    const outcome = await applyProfileImport(userId, plan, batchId);

    totals.cases += outcome.cases;
    totals.procedures += outcome.procedures;
    totals.edtEntries += outcome.edtEntries;
    totals.anaestheticCases += outcome.anaestheticCases;
    totals.total += outcome.total;
  }

  return totals;
}

/**
 * Undoes one import across every kind it created.
 *
 * Soft delete throughout, like every other removal in this app — the rows stay
 * and sync as deletions, so undoing an import on a phone undoes it on the
 * laptop too rather than leaving 1,296 anaesthetic cases alive there.
 */
export async function undoImportBatch(userId: string, batchId: string): Promise<number> {
  const cases = await casesRepository.undoImport(userId, batchId);
  const procedures = await proceduresRepository.undoImport(userId, batchId);
  const edtEntries = await edtRepository.undoImport(userId, batchId);
  const anaestheticCases = await anaestheticRepository.undoImport(userId, batchId);

  return cases + procedures + edtEntries + anaestheticCases;
}

export type ImportBatchSummary = { importBatchId: string; count: number; importedAt: Date };

/**
 * Every import batch on this device, across all four record kinds, for the
 * "Imports" list in Settings.
 *
 * Before the profile importer, this only ever needed to ask
 * `proceduresRepository` — every import was procedures. A sheet of acute pain
 * reviews or EDT sessions creates zero procedures, so a batch made entirely of
 * either was invisible to that list and, worse, silently un-undoable through
 * it: the Undo button called only `proceduresRepository.undoImport`, which
 * would remove nothing and leave the batch's real entries live with no way
 * back to them from this screen. This merges all four kinds' batches by id, so
 * every import that happened is one the trainee can see and take back.
 */
export async function listImportBatches(userId: string): Promise<ImportBatchSummary[]> {
  const [cases, procedures, edtEntries, anaestheticCases] = await Promise.all([
    casesRepository.importBatches(userId),
    proceduresRepository.importBatches(userId),
    edtRepository.importBatches(userId),
    anaestheticRepository.importBatches(userId),
  ]);

  const merged = new Map<string, ImportBatchSummary>();

  for (const batch of [...cases, ...procedures, ...edtEntries, ...anaestheticCases]) {
    const existing = merged.get(batch.importBatchId);

    if (existing === undefined) {
      merged.set(batch.importBatchId, { ...batch });
    } else {
      existing.count += batch.count;
      // The earliest timestamp across the four kinds is when the import as a
      // whole happened — every kind in one batch is written within the same
      // action, so this is only ever a matter of milliseconds between them.
      if (batch.importedAt.getTime() < existing.importedAt.getTime()) {
        existing.importedAt = batch.importedAt;
      }
    }
  }

  return [...merged.values()].sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
}
